import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PrimeReactProvider } from 'primereact/api';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Message } from 'primereact/message';
import API_BASE_URL from '../config/api';
import '../styles/InstagramAppReviewConnect.css';

const PROFILE_STORAGE_KEY = 'igAppReviewProfile';

function InstagramAppReviewConnect() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [account, setAccount] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    const oauthError = searchParams.get('error');
    const connected = searchParams.get('connected');
    const sessionId = searchParams.get('sid');

    if (oauthError) {
      setError(decodeURIComponent(oauthError));
      setSearchParams({}, { replace: true });
      return;
    }

    const loadProfile = async () => {
      if (connected === '1' && sessionId) {
        setLoadingProfile(true);
        setSuccessMessage('Your Instagram account is connected.');
        setSearchParams({}, { replace: true });

        try {
          const response = await fetch(`${API_BASE_URL}/api/admin/instagram/oauth-profile/${sessionId}`);
          const data = await response.json();

          if (response.ok && data.status === 'success') {
            setAccount(data.data);
            sessionStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(data.data));
          } else {
            setError(data.message || 'Failed to load connected account');
          }
        } catch (err) {
          setError('Failed to load connected account');
          console.error('OAuth profile fetch error:', err);
        } finally {
          setLoadingProfile(false);
        }
        return;
      }

      const stored = sessionStorage.getItem(PROFILE_STORAGE_KEY);
      if (stored) {
        try {
          setAccount(JSON.parse(stored));
        } catch (err) {
          console.error('Failed to parse stored profile:', err);
        }
      }
    };

    loadProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = () => {
    window.location.href = `${API_BASE_URL}/api/admin/instagram/connect`;
  };

  return (
    <PrimeReactProvider>
      <div className="ig-connect-page">
        <Card className="ig-connect-card">
          <div className="ig-connect-header">
            <i className="pi pi-instagram ig-connect-logo" aria-hidden="true" />
            <h1>Instagram</h1>
            <p className="ig-connect-description">
              Connect your professional Instagram account to link it with this app.
            </p>
          </div>

          {successMessage && (
            <Message severity="success" text={successMessage} className="ig-connect-message" />
          )}

          {error && (
            <Message severity="error" text={error} className="ig-connect-message" />
          )}

          {loadingProfile ? (
            <p className="ig-connect-status">Loading your account…</p>
          ) : account ? (
            <div className="ig-connect-profile">
              <Avatar
                image={account.profile_picture_url}
                label={account.username?.charAt(0)?.toUpperCase()}
                size="xlarge"
                shape="circle"
                className="ig-connect-avatar"
              />
              <div className="ig-connect-profile-info">
                <span className="ig-connect-profile-label">Connected account</span>
                <h2>@{account.username}</h2>
              </div>
            </div>
          ) : (
            !error && (
              <p className="ig-connect-status">No account connected yet.</p>
            )
          )}

          <Button
            type="button"
            label="Connect Instagram Account"
            icon="pi pi-instagram"
            onClick={handleConnect}
            size="large"
            className="ig-connect-button"
          />
        </Card>
      </div>
    </PrimeReactProvider>
  );
}

export default InstagramAppReviewConnect;
